/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
// import dayjs from "dayjs";
import dayjs from "dayjs";
import { AnimatePresence, motion } from "framer-motion";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSetRecoilState } from "recoil";
import { TempCampaignAtom } from "../../../../atoms/CampaignAtom";
import { NodeIndexAtom } from "../../../../atoms/NodeIndexAtom";
import { CampaignValidation } from "../../../../pages/create-campaign";
import {
  Campaign,
  IDelayTimer,
  INode,
  INodeTypes,
} from "../../../../types/CampaignTypes";
import DateTimePicker from "../../../DateTimePicker";
import IconBerserkerMail from "../../../Icon";
import StyledAutocompleteSelect from "../../../StyledAutoCompleteSelect/AutoCompleteSelect";
import StyledButton from "../../../StyledButton";
import StyledCheckBox from "../../../StyledCheckBox";
import StyledInput from "../../../StyledInput";
import StyledTimePicker from "../../../StyledTimePicker";
import Switch from "../../../Switch";
import { DefaultFormProps } from "./Form";
import {
  ConditionTimeOption,
  CONDITION_TIME_OPTIONS,
  DayWeekOption,
  DAY_WEEK_OPTIONS,
} from "./Options";

interface DelayUnit {
  value: string;
  name: string;
}

const DELAY_UNIT: DelayUnit[] = [
  {
    value: "1",
    name: "Minute(s)",
  },
  {
    value: "2",
    name: "Hour(s)",
  },
  {
    value: "3",
    name: "Day(s)",
  },
  {
    value: "4",
    name: "Week(s)",
  },
  {
    value: "5",
    name: "Month(s)",
  },
];

interface ArrowProps {
  selected: boolean;
}

const Container = styled.div`
  height: 100%;
  width: 100%;
  overflow-y: auto;
`;

const Arrow = styled(motion.img)<ArrowProps>`
  width: 15px;
  height: 15px;
  cursor: pointer;
`;

const Row = styled(motion.div)`
  border-bottom: 1px solid #dddddd;
  padding: 30px;
`;

const Column = styled.div`
  display: flex;
  flex-direction: row;
`;

const SpaceBetween = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
`;

const DelayContainer = styled(motion.div)`
  display: flex;
  flex-direction: column;
`;

const InputContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
`;

const SwitchContainer = styled.div`
  display: flex;
  align-items: center;
  margin: 20px 0px 10px;
`;

const SwitchText = styled.div`
  color: #2f2f2f;
  font-size: 14px;
  font-weight: 600;
  margin-right: 10px;
`;

const Description = styled(motion.div)`
  font-size: 14px;
  font-weight: normal;
  letter-spacing: 0px;
  color: #111111;
  margin: 12px 0px;
`;

const WeekContainer = styled.div`
  display: flex;
  flex-direction: row;
`;

const TimePickerContainer = styled.div`
  background-color: #ffffff;
  border: 1px solid #dddddd;
  border-radius: 4px;
  position: absolute;
  top: 70px;
  z-index: 10000;
`;

const TimeInputContainer = styled.div<{ dateLabel: string; disabled: boolean }>`
  display: flex;
  color: ${(props) => (props.dateLabel === "(MM/DD/YY" ? "#979797" : "#000")};
  cursor: pointer;
  border: solid 1px #cccccc;
  background-color: #ffffff;
  border-radius: 4px;
  height: 40px;
  padding: 10px;

  opacity: ${(props) => (props.disabled ? 0.5 : 1)};

  > div:first-of-type {
    margin-right: 7px;
  }
`;

const Title = styled.div`
  color: #2f2f2f;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 5px;
  display: flex;
`;

const WEEK_BUTTON = css`
  padding: 10px;
  font-size: 12px;
  background-color: white;
  color: #fb972e;
`;

const WEEK_BUTTON_ACTIVE = css`
  background-color: #fb972e;
  color: white;
`;

interface DelayUnit {
  value: string;
  name: string;
}

interface DelayMode {
  id: string;
  name: string;
}

const DELAY_MODE: DelayMode[] = [
  {
    id: "1",
    name: "Fixed date",
  },
  {
    id: "2",
    name: "Evergreen",
  },
];

const daysOfWeekLabels = ["S", "M", "T", "W", "T", "F", "S"];

export function validateDelay(node: INode): CampaignValidation {
  if (
    !node.delayTimer.constraints ||
    node.delayTimer.constraints.weekDays.length > 0
  ) {
    return {
      node,
      error: null,
    };
  } else {
    return {
      node: null,
      error: {
        toastInfo: {
          toastText: "Select at least one day to release your delay",
          toastType: "info",
        },
      },
    };
  }
}

const Delay = ({
  action,
  nodeIndex,
  tempCampaign,
}: DefaultFormProps & { tempCampaign: Campaign }) => {
  const setNodeIndex = useSetRecoilState(NodeIndexAtom);
  const setTempCampaign = useSetRecoilState(TempCampaignAtom);
  const [openDelay, setOpenDelay] = useState(true);
  const [newTime, setNewTime] = useState<number>(0);
  const [newTimeOutput, setNewTimeOutput] = useState<string>("0");

  const [isTimePickerActive, setTimePickerActivity] = useState(false);
  const [timeLabel, setTimeLabel] = useState("(HH:MM)");
  const [disabledToggle, setDisabledToggle] = useState<boolean>(false);
  const [step, setStep] = useState<"hour" | "minute">("hour");
  const [selectedTimeOption, setSelectedTimeOption] =
    useState<ConditionTimeOption>(CONDITION_TIME_OPTIONS[4]);

  // Delay unit
  const [delayUnit, setDelayUnit] = useState<DelayUnit>(DELAY_UNIT[0]);

  // Schedule mode
  const [delayMode, setDelayMode] = useState<DelayMode>(DELAY_MODE[0]);

  // Evergreen options
  const [hour, setHour] = useState<Date>(new Date(0));
  // const [dayOfMonth, setDayOfMonth] = useState<Date>(new Date());
  const [daysOfWeek, setDaysOfWeek] = useState<{
    [key: number]: DayWeekOption;
  }>({});

  // Fixed date option
  const [fixedDate, setFixedDate] = useState<Date>(new Date());

  const numberInputRef = useRef<HTMLInputElement>(null);

  const bottomNode = tempCampaign?.nodes[nodeIndex];
  const currentNode = tempCampaign?.nodes[nodeIndex - 1];
  const currentNodeType = currentNode?.type;

  const currentNodeId = currentNode?._id;
  const bottomNodeId = bottomNode?._id;

  const returnText = useMemo(() => {
    switch (bottomNode.type) {
      case INodeTypes.Email:
        return "Wait for a period of time before sending this email";
      case INodeTypes.WebHook:
        return "Wait for a period of time before triggering this webhook";
      case INodeTypes.ManageTag:
        return "Wait for a period of time before applying or removing this tag";
      default:
        return "Wait for a period of time before sending this email";
    }
  }, [bottomNode.type]);

  useEffect(() => {
    if (nodeIndex <= 0) return;
    if (currentNodeType !== INodeTypes.DelayTimer) return;
    if (newTime === 0) {
      setDisabledToggle(true);
      return;
    }

    // @ts-ignore
    const toSend: IDelayTimer = {
      unit: parseInt(delayUnit.value),
      delay: newTime,
      ...(delayMode.id === "2" && {
        constraints: {
          weekDays: Object.keys(daysOfWeek).map((day) => parseInt(day)),
          time: hour,
        },
      }),
    };

    const delayIndex = nodeIndex - 1;

    const newDelayAction: INode = {
      _id: currentNodeId || undefined,
      name: "",
      type: INodeTypes.DelayTimer,
      delayTimer: toSend,
    };

    setTempCampaign({
      ...tempCampaign,
      nodes: [
        ...tempCampaign.nodes.slice(0, delayIndex),
        newDelayAction,
        ...tempCampaign.nodes.slice(delayIndex + 1),
      ],
    });
    setDisabledToggle(false);
  }, [
    currentNodeType,
    currentNodeId,
    nodeIndex,
    delayUnit,
    newTime,
    delayMode,
    daysOfWeek,
    hour,
  ]);

  useEffect(() => {
    if (nodeIndex <= 0) return;
    if (
      daysOfWeek[0] &&
      daysOfWeek[1] &&
      daysOfWeek[2] &&
      daysOfWeek[3] &&
      daysOfWeek[4] &&
      daysOfWeek[5] &&
      daysOfWeek[6]
    ) {
      if (selectedTimeOption === CONDITION_TIME_OPTIONS[0]) return;
      setSelectedTimeOption(CONDITION_TIME_OPTIONS[0]);
      return;
    }

    if (
      daysOfWeek[0] &&
      !daysOfWeek[1] &&
      !daysOfWeek[2] &&
      !daysOfWeek[3] &&
      !daysOfWeek[4] &&
      !daysOfWeek[5] &&
      daysOfWeek[6]
    ) {
      if (selectedTimeOption === CONDITION_TIME_OPTIONS[3]) return;
      setSelectedTimeOption(CONDITION_TIME_OPTIONS[3]);
      return;
    }

    if (
      !daysOfWeek[0] &&
      daysOfWeek[1] &&
      daysOfWeek[2] &&
      daysOfWeek[3] &&
      daysOfWeek[4] &&
      daysOfWeek[5] &&
      !daysOfWeek[6]
    ) {
      if (selectedTimeOption === CONDITION_TIME_OPTIONS[2]) return;
      setSelectedTimeOption(CONDITION_TIME_OPTIONS[2]);
      return;
    }
    if (selectedTimeOption !== CONDITION_TIME_OPTIONS[4])
      setSelectedTimeOption(CONDITION_TIME_OPTIONS[4]);
  }, [daysOfWeek]);

  useEffect(() => {
    if (nodeIndex <= 0) return;
    if (hour) {
      setTimeLabel(dayjs(hour).format("h:mm a"));
    }
  }, [hour]);

  const currentDelayTimer = currentNode?.delayTimer;

  useEffect(() => {
    if (currentNode?.type !== INodeTypes.DelayTimer) {
      setTimeLabel("(HH:MM)");
      setStep("hour");
      setSelectedTimeOption(CONDITION_TIME_OPTIONS[4]);
      let today = new Date();
      today.setHours(0, 0, 0, 0);
      setHour(today);
      setDaysOfWeek({});
      // Delay unit
      setDelayUnit(DELAY_UNIT[0]);
      setNewTime(0);
      setDisabledToggle(true);
      setNewTimeOutput(numberInputRef.current?.value === "" ? "" : "0");
      setOpenDelay(true);
      setDelayMode(DELAY_MODE[0]);
      return;
    }

    if (nodeIndex <= 0) return;

    const delayTimer = currentDelayTimer || 0;

    // @ts-ignore
    deserializeDelay(delayTimer);
    setOpenDelay(true);
  }, [action, nodeIndex]);

  function deserializeDelay(delayTimer: IDelayTimer) {
    const { delay, unit, constraints } = delayTimer;
    setDelayUnit(DELAY_UNIT[unit - 1]);
    setNewTime(delay);
    setNewTimeOutput(delay + "");

    if (constraints) {
      setDelayMode(DELAY_MODE[1]);
      setDaysOfWeek(
        constraints.weekDays
          .map((weekDay) => DAY_WEEK_OPTIONS[weekDay])
          .reduce((o, key) => ({ ...o, [key.id + ""]: key }), {})
      );
      let timeOffset = dayjs.utc(constraints.time);
      let today = dayjs.utc();
      today = today.utc().set("hours", timeOffset.utc().get("hours"));
      today = today.utc().set("minutes", timeOffset.utc().get("minutes"));
      today = today.utc().set("seconds", timeOffset.utc().get("seconds"));
      today = today
        .utc()
        .set("milliseconds", timeOffset.utc().get("milliseconds"));

      setHour(today.local().toDate());
    } else {
      setDelayMode(DELAY_MODE[0]);
    }
  }

  function handleChangeTime(event: React.ChangeEvent<HTMLInputElement>) {
    let newTimeEntered = event.target.value?.replace(".", "");
    if (!/^(\s*|\d+)$/.test(newTimeEntered)) return;
    const newTimeValid = parseInt(newTimeEntered) > 0;
    const currentNodeId = undefined;
    const delayIndex = nodeIndex - 1;

    if (newTimeValid && currentNode?.type !== INodeTypes.DelayTimer) {
      // @ts-ignore
      const toSend: IDelayTimer = {
        unit: parseInt(delayUnit.value),
        delay: parseInt(newTimeEntered),
        ...(delayMode.id === "2" && {
          constraints: {
            weekDays: Object.keys(daysOfWeek).map((day) => parseInt(day)),
            time: hour,
          },
        }),
      };

      const newDelayAction: INode = {
        _id: currentNodeId,
        name: "",
        type: INodeTypes.DelayTimer,
        delayTimer: toSend,
      };

      const newNodes = [...tempCampaign.nodes];
      newNodes.splice(nodeIndex, 0, newDelayAction);

      setTempCampaign((tempCampaign) => ({
        ...tempCampaign,
        nodes: newNodes,
      }));
      setNodeIndex(delayIndex + 2);
      setDisabledToggle(false);
    } else if (!newTimeValid && currentNode?.type === INodeTypes.DelayTimer) {
      if (nodeIndex <= 0) return;
      setTempCampaign((tempCampaign) => {
        const newNodes = [...tempCampaign.nodes];
        newNodes.splice(delayIndex, 1);

        return {
          ...tempCampaign,
          nodes: newNodes,
        };
      });
      setNodeIndex(nodeIndex - 1);
      setDisabledToggle(true);
    }

    setNewTimeOutput(newTimeEntered);
    setNewTime(+newTimeEntered);
  }

  function selectEveryDay() {
    if (selectedTimeOption.id === "1") {
      setSelectedTimeOption(CONDITION_TIME_OPTIONS[4]);
      setDaysOfWeek({});
      return;
    }

    setSelectedTimeOption(CONDITION_TIME_OPTIONS[0]);
    setDaysOfWeek({
      [0]: DAY_WEEK_OPTIONS[0],
      [1]: DAY_WEEK_OPTIONS[1],
      [2]: DAY_WEEK_OPTIONS[2],
      [3]: DAY_WEEK_OPTIONS[3],
      [4]: DAY_WEEK_OPTIONS[4],
      [5]: DAY_WEEK_OPTIONS[5],
      [6]: DAY_WEEK_OPTIONS[6],
    });
  }

  function selectDaysOfWeek() {
    if (selectedTimeOption.id === "3") {
      setSelectedTimeOption(CONDITION_TIME_OPTIONS[4]);
      setDaysOfWeek({});
      return;
    }

    setSelectedTimeOption(CONDITION_TIME_OPTIONS[2]);
    setDaysOfWeek({
      [1]: DAY_WEEK_OPTIONS[1],
      [2]: DAY_WEEK_OPTIONS[2],
      [3]: DAY_WEEK_OPTIONS[3],
      [4]: DAY_WEEK_OPTIONS[4],
      [5]: DAY_WEEK_OPTIONS[5],
    });
  }

  function selectWeekend() {
    if (selectedTimeOption.id === "4") {
      setSelectedTimeOption(CONDITION_TIME_OPTIONS[4]);
      setDaysOfWeek({});
      return;
    }

    setSelectedTimeOption(CONDITION_TIME_OPTIONS[3]);
    setDaysOfWeek({
      [0]: DAY_WEEK_OPTIONS[0],
      [6]: DAY_WEEK_OPTIONS[6],
    });
  }

  function setDay(index: number) {
    if (daysOfWeek[index]) {
      const copy = { ...daysOfWeek };
      delete copy[index];
      setDaysOfWeek(copy);
    } else {
      setDaysOfWeek({
        ...daysOfWeek,
        [index]: DAY_WEEK_OPTIONS[index],
      });
    }
  }

  function handleShowTimePicker() {
    if (action === "create" && currentNodeId) return;
    setTimePickerActivity(!isTimePickerActive);
  }

  const disabled = action === "create" && !!bottomNodeId;

  return (
    <Row>
      <SpaceBetween
        style={{ cursor: "pointer" }}
        onClick={() => setOpenDelay(!openDelay)}
      >
        <Title>
          <IconBerserkerMail name="delay" />
          &nbsp;DELAY
        </Title>

        <Arrow
          initial={{ rotate: 0 }}
          animate={openDelay ? { rotate: 0 } : { rotate: 180 }}
          transition={{ ease: "easeOut", duration: 0.3 }}
          src={"../../../icons/arrow-up.svg"}
          selected={openDelay}
        />
      </SpaceBetween>

      <AnimatePresence>
        {openDelay && (
          <>
            <Description
              initial={{ opacity: 1 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ ease: "easeOut", duration: 0.3 }}
            >
              {returnText}
            </Description>
            <DelayContainer
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ ease: "easeOut", duration: 0.3 }}
            >
              <InputContainer>
                <StyledInput
                  disabled={disabled}
                  min="0"
                  step="1"
                  placeholder="Time"
                  ref={numberInputRef}
                  onKeyDownCapture={(event) => {
                    const mod =
                      event.metaKey ||
                      event.ctrlKey ||
                      event.keyCode === 37 ||
                      event.keyCode === 39;
                    if (
                      !(
                        event.keyCode === 8 ||
                        event.keyCode === 46 ||
                        mod ||
                        !isNaN(Number(event.key))
                      )
                    ) {
                      event.preventDefault();
                      event.stopPropagation();
                    }
                  }}
                  value={newTimeOutput}
                  onChange={handleChangeTime}
                  onBlur={() => {
                    const event = {
                      target: { value: newTimeOutput || "0" },
                    } as React.ChangeEvent<HTMLInputElement>;
                    handleChangeTime(event);
                  }}
                  css={css`
                    width: 100px;
                    margin: 10px 20px 10px 0;
                  `}
                />
                <div
                  css={css`
                    width: 170px;
                    margin: 10px 20px 10px 0;
                  `}
                >
                  <StyledAutocompleteSelect
                    forceHideNoOption={true}
                    disabled={disabled}
                    options={DELAY_UNIT}
                    selectedOption={delayUnit}
                    onOptionSelect={(opt) => setDelayUnit(opt)}
                    placeholder={"Period"}
                    nonEditableInput={true}
                  />
                </div>
              </InputContainer>
              <SwitchContainer>
                <SwitchText>Delay Options</SwitchText>
                <Switch
                  disabled={disabled || disabledToggle}
                  noInfo
                  toggled={delayMode.id === "2"}
                  onClick={() => {
                    if (
                      !disabled &&
                      disabledToggle &&
                      numberInputRef?.current
                    ) {
                      numberInputRef.current.focus();
                    }
                  }}
                  whenToggled={(tog) =>
                    setDelayMode(
                      delayMode.id === "2" ? DELAY_MODE[0] : DELAY_MODE[1]
                    )
                  }
                />
              </SwitchContainer>
              {delayMode.id === "2" ? (
                <>
                  <Column>
                    {selectedTimeOption.id === "2" ? (
                      <DateTimePicker
                        disabled={disabled}
                        date={new Date()}
                        showDatePicker
                        input
                        showTimePicker
                        onDateSelect={() => {}}
                        onTimeSelect={() => {}}
                      />
                    ) : (
                      <Row
                        css={css`
                          padding: 0;
                          border: none;
                        `}
                      >
                        <Column
                          css={css`
                            position: relative;
                          `}
                        >
                          <Column
                            css={css`
                              display: flex;
                              flex-direction: row;
                              align-items: center;
                              margin: 20px 0;
                            `}
                          >
                            Hour:
                            <Container
                              onClick={handleShowTimePicker}
                              css={css`
                                margin-left: 20px;
                              `}
                            >
                              <TimeInputContainer
                                dateLabel={timeLabel}
                                disabled={disabled}
                              >
                                <div
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleShowTimePicker();
                                  }}
                                >
                                  {timeLabel}
                                </div>
                              </TimeInputContainer>
                            </Container>
                          </Column>
                          {isTimePickerActive && (
                            <TimePickerContainer>
                              <StyledTimePicker
                                step={step}
                                setStep={setStep}
                                goBack={() => {
                                  handleShowTimePicker();
                                  setStep("hour");
                                }}
                                noLabel
                                date={hour}
                                minuteInterval={5}
                                onSelect={({ hour, minute }) => {
                                  handleShowTimePicker();
                                  let selectedDateTime = dayjs();
                                  selectedDateTime = selectedDateTime.set(
                                    "hours",
                                    hour as number
                                  );
                                  selectedDateTime = selectedDateTime.set(
                                    "minutes",
                                    minute as number
                                  );
                                  selectedDateTime = selectedDateTime.set(
                                    "seconds",
                                    0
                                  );
                                  setHour(selectedDateTime.toDate());
                                  setStep("hour");
                                }}
                              />
                            </TimePickerContainer>
                          )}
                        </Column>
                        <Column>
                          <StyledButton
                            disable={disabled}
                            onClick={selectEveryDay}
                            css={css`
                              ${WEEK_BUTTON}
                              ${selectedTimeOption.id === "1"
                                ? WEEK_BUTTON_ACTIVE
                                : ""}
                            `}
                          >
                            Any day
                          </StyledButton>
                          <StyledButton
                            disable={disabled}
                            onClick={selectDaysOfWeek}
                            css={css`
                              ${WEEK_BUTTON}
                              ${selectedTimeOption.id === "3"
                                ? WEEK_BUTTON_ACTIVE
                                : ""}
                              margin: 0 10px;
                            `}
                          >
                            Days of the week
                          </StyledButton>
                          <StyledButton
                            disable={disabled}
                            onClick={selectWeekend}
                            css={css`
                              ${WEEK_BUTTON}
                              ${selectedTimeOption.id === "4"
                                ? WEEK_BUTTON_ACTIVE
                                : ""}
                            `}
                          >
                            Weekend
                          </StyledButton>
                        </Column>
                        <WeekContainer
                          css={css`
                            justify-content: space-between;
                            margin-top: 10px;
                          `}
                        >
                          {DAY_WEEK_OPTIONS.map((day, index) => (
                            <Row
                              css={css`
                                padding: 0;
                              `}
                            >
                              <StyledCheckBox
                                disabled={disabled}
                                customCss={css`
                                  opacity: ${disabled ? 0.5 : 1};
                                `}
                                check={!!daysOfWeek[index]}
                                handleCheck={() => {
                                  setDay(index);
                                }}
                              />
                              <SwitchText
                                css={css`
                                  margin: 0;
                                  text-align: center;
                                  cursor: default;
                                `}
                                onClick={() => {
                                  setDay(index);
                                }}
                              >
                                {daysOfWeekLabels[index]}
                              </SwitchText>
                            </Row>
                          ))}
                        </WeekContainer>
                      </Row>
                    )}
                  </Column>
                </>
              ) : null}
            </DelayContainer>
          </>
        )}
      </AnimatePresence>
    </Row>
  );
};

export default Delay;
